import { Icon } from '@iconify/react/dist/iconify.js';
import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ConfigContext } from '../context/GlobalContext';
import * as actionType from "../context/actions";
import { Languages } from '../Utils/Languages';
import { FreePlanLimitReachedModal } from './FreePlanLimitReached';
import QuotationTableDataLayer from './QuotationTableDataLayer';
import BusinessDropdown from './child/BusinessDropdown';

const QuotationListLayer = ({ businessData, invoiceData }) => {
    const { state, dispatch } = useContext(ConfigContext);
    const navigate = useNavigate();

    const [selectedBusiness, setSelectedBusiness] = useState("null");
    const [isDisplayFreePlanReachedModel, setIsDisplayFreePlanReachedModel] = useState(false);
    const [limitPlanName, setLimitPlanName] = useState("Free Plan");
    const [limitValue, setLimitValue] = useState(1);

    // ================= PLAN CONFIG =================
    const starterPlanName = process.env.REACT_APP_STARTER_PLAN_NAME;
    const professionalPlanName = process.env.REACT_APP_PROFESSIONAL_PLAN_NAME;
    const agencyPlanName = process.env.REACT_APP_AGENCY_PLAN_NAME;

    const userPlanName =
        state?.PAYMENT?.[state?.PAYMENT?.length - 1]?.planName || "Free Plan";

    const isFreePlan = userPlanName === "Free Plan";
    const isStarterPlan = userPlanName === starterPlanName;
    const isProfessionalPlan = userPlanName === professionalPlanName;
    const isAgencyPlan = userPlanName === agencyPlanName;
    // =================================================

    useEffect(() => {
        const selected =
            state?.SELECTEDBUSINESS ||
            businessData?.[businessData.length - 1]?.companyId;

        setSelectedBusiness(selected);
        dispatch({ type: actionType.SELECTEDBUSINESS, payload: selected });
    }, [dispatch]);

    const handleSelection = (businessId) => {
        setSelectedBusiness(businessId);
        dispatch({ type: actionType.SELECTEDBUSINESS, payload: businessId });
    };

    const sortedBusinesses = [...(state?.BUSINESSES || [])].sort(
        (a, b) => Number(b.creationDate) - Number(a.creationDate)
    );

    // ===========================
    // BUSINESS ACCESS LIMIT
    // ===========================
    let allowedBusinessCount = 1; // Free & Starter

    if (isProfessionalPlan) {
        allowedBusinessCount = 3;
    }

    if (isAgencyPlan) {
        allowedBusinessCount = sortedBusinesses.length;
    }

    const ownBusinesses = sortedBusinesses.filter(b => !b.isShared);
    const sharedBusinesses = sortedBusinesses.filter(b => b.isShared);

    const allowedOwnIds = ownBusinesses
        .slice(-allowedBusinessCount)
        .map(b => String(b.companyId));
    const sharedIds = sharedBusinesses.map(b => String(b.companyId));

    const allAllowedIds = [...allowedOwnIds, ...sharedIds];

    const filteredInvoiceData = (invoiceData || []).filter(invoice =>
        allAllowedIds.includes(String(invoice.companyId))
    );

    return (
        <div className="card basic-data-table">
            <div className="card-header d-flex flex-wrap align-items-center justify-content-between gap-3">

                <BusinessDropdown
                    businesses={sortedBusinesses}
                    selectedCompanyId={state?.SELECTEDBUSINESS}
                    onBusinessChange={handleSelection}
                    onPricingNavigate={() => navigate("/pricing")}
                    showAllOption={true}
                    isAllowedCheck={(business) => {
                        if (business === "null") return isAgencyPlan;

                        const ownBusinesses = sortedBusinesses.filter(b => !b.isShared);
                        const allowedOwnIds = ownBusinesses
                            .slice(-allowedBusinessCount)
                            .map(b => b.companyId);

                        return (
                            business.isShared ||
                            isAgencyPlan ||
                            allowedOwnIds.includes(business.companyId)
                        );
                    }}
                />

                {/* ================= CREATE QUOTATION BUTTON ================= */}
                <button
                    onClick={() => {

                        if (!state?.USERID) {
                            dispatch({ type: actionType.GUESTMODELPOPUP, payload: true });
                            return;
                        }

                        if (!state?.BUSINESSES?.length) {
                            navigate("/business-add");
                            return;
                        }

                        const currentYear = new Date().getFullYear();
                        const currentMonth = new Date().getMonth();

                        const freePlanLimit = Number(process.env.REACT_APP_FREE_PLAN_INVOICE_LIMIT);
                        const starterPlanLimit = Number(process.env.REACT_APP_STARTER_PLAN_INVOICE_LIMIT);

                        const currentMonthQuotation = (state?.QUOTATION || []).filter(q => {
                            const business = state?.BUSINESSES?.find(b => String(b.companyId) === String(q.companyId));
                            if (business?.isShared) return false;
                            const date = new Date(Number(q.creationDate));
                            return (
                                date.getFullYear() === currentYear &&
                                date.getMonth() === currentMonth
                            );
                        }).length;

                        const currentYearQuotation = (state?.QUOTATION || []).filter(q => {
                            const business = state?.BUSINESSES?.find(b => String(b.companyId) === String(q.companyId));
                            if (business?.isShared) return false;
                            const date = new Date(Number(q.creationDate));
                            return date.getFullYear() === currentYear;
                        }).length;

                        const selectedBusinessData = state?.BUSINESSES?.find(b => String(b.companyId) === String(selectedBusiness));
                        const isSharedSelected = selectedBusinessData?.isShared === true;

                        // Bypass limits for shared businesses
                        if (isSharedSelected) {
                            navigate("/quotation-add");
                            return;
                        }

                        // Free
                        if (isFreePlan) {
                            if (currentMonthQuotation >= freePlanLimit) {
                                setLimitPlanName("Free Plan");
                                setLimitValue(freePlanLimit);
                                setIsDisplayFreePlanReachedModel(true);
                                return;
                            }
                            navigate("/quotation-add");
                            return;
                        }

                        // STARTER / PROFESSIONAL / AGENCY → unlimited
                        navigate("/quotation-add");
                    }}
                    className="btn btn-primary-600 bg-primary border-primary px-18 py-11"
                >
                    <i className="ri-add-line" /> Create Quotation
                </button>

            </div>

            <FreePlanLimitReachedModal
                isOpen={isDisplayFreePlanReachedModel}
                onClose={() => setIsDisplayFreePlanReachedModel(false)}
                limit={limitValue}
                type="Quotation"
                planName={limitPlanName}
            />

            <QuotationTableDataLayer
                InvoicesData={filteredInvoiceData}
                selectedBusiness={selectedBusiness}
            />
        </div>
    );
};

export default QuotationListLayer;
